import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { INote } from '@interfaces/INoteData';
import { IStudentResults } from '@interfaces/IStudentResults';
import { IUsersPagination } from '@interfaces/IUser';
import { IAppState } from '@myStore/app.state';
import {
  EUsersViewActions,
  GenerateStudentResultsFailure,
  GenerateStudentResultsRequest,
  GenerateStudentResultsSuccess,
  GetStudentFinalResultsFailure,
  GetStudentFinalResultsRequest,
  GetStudentFinalResultsSuccess,
  GetStudentGroupsResultsFailure,
  GetStudentGroupsResultsRequest,
  GetStudentGroupsResultsSuccess,
  GetStudentsFailure,
  GetStudentsRequest,
  GetStudentsSuccess,
  GetTeachersFailure,
  GetTeachersRequest,
  GetTeachersSuccess,
  GetUserNoteByIdFailure,
  GetUserNoteByIdRequest,
  GetUserNoteByIdSuccess,
  GetUserNotesFailure,
  GetUsersFailure,
  GetUsersNotesRequest,
  GetUsersNotesSuccess,
  GetUsersRequest,
  GetUsersSuccess,
} from '@myStore/users-view/users-view.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UsersViewService } from '@services/usersView/usersView.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class UsersViewEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _toastr: ToastrService,
    private _router: Router,
    private _usersViewService: UsersViewService
  ) {}

  $getUsers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUsersRequest>(EUsersViewActions.GetUsersRequest),
        switchMap(action => {
          return this._usersViewService.getUsers(action.options?.qparams).pipe(
            map(users => {
              this._store.dispatch(new GetUsersSuccess(users));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При получении пользователей возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetUsersFailure(err.error));
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getStudents = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetStudentsRequest>(EUsersViewActions.GetStudentsRequest),
        map(action => action),
        switchMap(action => {
          return this._usersViewService
            .getStudents(action.groupId, action.qParams)
            .pipe(
              map((students: IUsersPagination) => {
                this._store.dispatch(new GetStudentsSuccess(students));
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При получении студентов возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new GetStudentsFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getTeachers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetTeachersRequest>(EUsersViewActions.GetTeachersRequest),
        switchMap(action => {
          return this._usersViewService
            .getGroupTeachers(action.groupId, action.qParams)
            .pipe(
              map((teachers: IUsersPagination) => {
                this._store.dispatch(new GetTeachersSuccess(teachers));
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При получении преподавателей возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new GetTeachersFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getNoteById = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUserNoteByIdRequest>(
          EUsersViewActions.GetUserNoteByIdRequest
        ),
        switchMap(action => {
          return this._usersViewService.getNoteById(action.id).pipe(
            map((note: INote) => {
              this._store.dispatch(new GetUserNoteByIdSuccess(note));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При получении примечаний о пользователе по id возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetUserNoteByIdFailure(err.error));
              return err.statusText;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getNotes = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetUsersNotesRequest>(EUsersViewActions.GetUsersNotesRequest),
        switchMap(action => {
          return this._usersViewService.getNotes(action.params).pipe(
            map((note: INote[]) => {
              this._store.dispatch(new GetUsersNotesSuccess(note));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При получении примечаний возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetUserNotesFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getGroupsResults = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetStudentGroupsResultsRequest>(
          EUsersViewActions.GetStudentGroupsResultsRequest
        ),
        switchMap(action => {
          return this._usersViewService.getGroupsResults(action.userId).pipe(
            map((results: IStudentResults[]) => {
              this._store.dispatch(new GetStudentGroupsResultsSuccess(results));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить успеваемость студента'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(
                new GetStudentGroupsResultsFailure(err.error)
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $generateStudentResults = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GenerateStudentResultsRequest>(
          EUsersViewActions.GenerateStudentResultsRequest
        ),
        switchMap(action => {
          return this._usersViewService
            .generateStudentResults(action.id, action.periods)
            .pipe(
              map(res => {
                this._store.dispatch(new GenerateStudentResultsSuccess(res));
              }),
              catchError(err => {
                let message =
                  'Не получилось сгенерировать итоговую успеваемость';

                if (err.error.periods) {
                  message = err.error.periods;
                }

                this._toastr.error(message);
                this._store.dispatch(
                  new GenerateStudentResultsFailure(err.error)
                );
                return err;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $studentFinalResults = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetStudentFinalResultsRequest>(
          EUsersViewActions.GetStudentFinalResultsRequest
        ),
        switchMap(action => {
          return this._usersViewService.getStudentFinalResults(action.id).pipe(
            map(res => {
              this._store.dispatch(new GetStudentFinalResultsSuccess(res));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить успеваемость студента'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(
                new GetStudentFinalResultsFailure(err.error)
              );
              return err;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
